import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { ImageSelectSpeakExercise, shows } from '../../../student/mockLessonContent';
import { BLUE, cardSx, INTER, NAVY, POPPINS, SLATE, SURFACE_SOFT } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import { ZoomButton } from '../../../student/components/ImageZoom';
import OptionalSupport from '../../../student/components/exercises/OptionalSupport';
import RecordAnswerPanel from '../../../student/components/exercises/RecordAnswerPanel';
import SpeakingHero from '../../../student/components/exercises/SpeakingHero';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';

interface ImageSelectSpeakExerciseCardProps {
    exercise: ImageSelectSpeakExercise;
    onReady: ExerciseReady;
}

// "Dialog w/ Image Select & Speak" — a genuine question with no single right
// answer, which gives the student some agency. Every picture is valid; the pick
// locks, then that picture's own cue and sentence are spoken and graded.
export default function ImageSelectSpeakExerciseCard({ exercise, onReady }: ImageSelectSpeakExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [pickedIndex, setPickedIndex] = React.useState<number | null>(null);
    const picked = pickedIndex == null ? null : exercise.options[pickedIndex];
    const labelled = shows(exercise, 'characters');

    return (
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <SpeakingHero
                label="CHOOSE AND SPEAK"
                hanzi={exercise.question.hanzi}
                pinyin={shows(exercise, 'pinyin') ? exercise.question.pinyin : undefined}
            />

            <Box sx={{ p: desktop ? 3 : 2.5 }}>
                {exercise.teacher?.intro && <Box sx={{ mb: 2 }}><TeacherNote text={exercise.teacher.intro} /></Box>}
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '14px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${BLUE}1A`,
                    }}>
                        <VolumeUpRoundedIcon sx={{ color: BLUE }} />
                    </Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, flex: 1, minWidth: 0 }}>
                        {exercise.prompt}
                    </Typography>
                    <AudioPlayButton ariaLabel="Play the question" size={40} />
                </Stack>

                <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: desktop ? `repeat(${Math.min(exercise.options.length, 4)}, minmax(0, 1fr))` : '1fr 1fr', gap: 1.5 }}>
                    {exercise.options.map((option, index) => {
                        const isPicked = pickedIndex === index;
                        const locked = pickedIndex != null;
                        return (
                            <Box key={option.hanzi} sx={{ position: 'relative' }}>
                                <ButtonBase
                                    onClick={() => setPickedIndex(index)}
                                    disabled={locked}
                                    aria-label={`Choose picture: ${option.cue}`}
                                    sx={{
                                        width: '100%', flexDirection: 'column', p: 1.75, minHeight: labelled ? 112 : 96, borderRadius: '18px',
                                        backgroundColor: isPicked ? `${BLUE}14` : SURFACE_SOFT,
                                        border: `2px solid ${isPicked ? BLUE : 'transparent'}`,
                                        opacity: locked && !isPicked ? 0.45 : 1,
                                        transition: 'background-color 150ms ease, border-color 150ms ease, opacity 150ms ease',
                                    }}
                                >
                                    <Typography role="img" aria-label={option.cue} sx={{ fontSize: 40, lineHeight: 1 }}>{option.emoji}</Typography>
                                    {labelled && (
                                        <>
                                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY, mt: 1 }}>{option.hanzi}</Typography>
                                            {shows(exercise, 'pinyin') && <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{option.pinyin}</Typography>}
                                        </>
                                    )}
                                </ButtonBase>
                                <ZoomButton emoji={option.emoji} alt={option.cue} />
                            </Box>
                        );
                    })}
                </Box>
                {picked && (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.25 }}>
                        <LockRoundedIcon sx={{ fontSize: 15, color: SLATE }} />
                        <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE }}>Your choice is locked in. Any picture is a good answer.</Typography>
                    </Stack>
                )}

                {picked && (
                    <>
                        <Box sx={{ mt: 2.5 }}><TeacherNote text={picked.cue} /></Box>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE, mt: 2.5, mb: 1 }}>
                            NOW SAY IT
                        </Typography>
                        <Box sx={{ p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 19, color: NAVY, lineHeight: 1.4 }}>
                                    {picked.sentence.hanzi}
                                </Typography>
                                {shows(exercise, 'pinyin') && (
                                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>{picked.sentence.pinyin}</Typography>
                                )}
                                {shows(exercise, 'english') && picked.sentence.english && (
                                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>{picked.sentence.english}</Typography>
                                )}
                            </Box>
                            <AudioPlayButton ariaLabel="Play the model answer" size={40} />
                        </Box>
                        <OptionalSupport content={picked} display={{ ...exercise.display, pinyin: shows(exercise, 'pinyin') }} />
                        <RecordAnswerPanel
                            grades={picked.sentence.grades}
                            takes={picked.sentence.takes}
                            correctText={exercise.teacher?.spokenCorrect}
                            incorrectText={exercise.teacher?.spokenIncorrect}
                            onComplete={() => onReady('done')}
                            onSkippable={() => onReady('skip')}
                        />
                    </>
                )}
                <ExerciseSupport exercise={exercise} />
            </Box>
        </Box>
    );
}
